/**
 * ArchiveScopeDialog Component
 *
 * Asked before archiving when the current user may archive for everyone
 * (they created the chat, or they are a church owner/admin): archive just for
 * themselves, or for every participant.
 *
 * Same markup/hooks as DeleteMessageDialog so the classic Tailwind look and the
 * Studio overrides (data-ui="scrim" / "float" / "dialog-icon-tile" / "dlg-title"
 * / "dlg-f") both apply with no new CSS.
 */

import React, { useEffect, useRef } from 'react';
import { Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function ArchiveScopeDialog({
  isOpen,
  onClose,
  onArchiveForMe,
  onArchiveForEveryone,
  isArchiving = false
}) {
  const firstButtonRef = useRef(null);

  // Escape closes, and focus moves into the dialog on open
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    firstButtonRef.current?.focus();
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      data-ui="scrim"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="archive-scope-dialog-overlay"
    >
      <div
        data-ui="float"
        className="bg-card rounded-2xl shadow-xl max-w-sm w-full mx-4 overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="archive-scope-dialog-title"
        data-testid="archive-scope-dialog"
      >
        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex items-center gap-3 mb-3">
            <div
              data-ui="dialog-icon-tile"
              className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center"
            >
              <Archive className="w-5 h-5 text-slate-600 dark:text-slate-300" />
            </div>
            <h3
              id="archive-scope-dialog-title"
              data-ui="dlg-title"
              className="text-lg font-semibold text-slate-900 dark:text-slate-100"
            >
              Archive this chat
            </h3>
          </div>

          <p className="text-sm text-slate-600 dark:text-slate-300">
            Archive it just for you, or for everyone in the chat? A new message will bring it back.
          </p>
        </div>

        {/* Actions */}
        <div data-ui="dlg-f" className="px-6 pb-6 flex flex-col gap-2">
          <Button
            ref={firstButtonRef}
            variant="outline"
            onClick={onArchiveForMe}
            disabled={isArchiving}
            className="w-full"
            data-testid="archive-scope-me"
          >
            Just for me
          </Button>

          <Button
            onClick={onArchiveForEveryone}
            disabled={isArchiving}
            className="w-full"
            data-testid="archive-scope-everyone"
          >
            {isArchiving ? 'Archiving...' : 'For everyone'}
          </Button>

          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isArchiving}
            className="w-full"
            data-testid="archive-scope-cancel"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
